import React, { useEffect, useRef, useState } from "react";

import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Typography,
} from "@mui/material";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

import AppButton from "../../ui/AppButton";
import { updateEmployee } from "../../services/employeeService";
import {
  aiSyncDepartments,
  createDepartment,
  getDepartments,
} from "../../services/departmentService";

const MISSING_DESIGNATION_VALUES = new Set(["", "-", "na", "n/a", "none", "null"]);

const isMissingDesignation = (value) =>
  MISSING_DESIGNATION_VALUES.has((value || "").trim().toLowerCase());

const EVENT_META = {
  info:    { icon: InfoOutlinedIcon,           color: "#64748b" },
  updated: { icon: SyncAltIcon,               color: "#2563eb" },
  skipped: { icon: CheckCircleOutlineIcon,    color: "#16a34a" },
  missing: { icon: WarningAmberOutlinedIcon,  color: "#f97316" },
};

function LogLine({ event }) {
  const meta = EVENT_META[event.type] || EVENT_META.info;
  const Icon = meta.icon;
  return (
    <Box display="flex" alignItems="flex-start" gap={1} py={0.35}>
      <Icon sx={{ fontSize: 15, color: meta.color, flexShrink: 0, mt: "2px" }} />
      <Typography fontSize={12.5} sx={{ color: "text.primary", lineHeight: 1.5 }}>
        {event.message}
      </Typography>
    </Box>
  );
}

function EmployeeDeptSyncDialog({ open, onClose, employees, departments, onDone }) {
  const [lines, setLines] = useState([]);
  const [isDone, setIsDone] = useState(false);
  const [summary, setSummary] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const logEndRef = useRef(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [lines]);

  useEffect(() => {
    if (!open) return;

    setLines([]);
    setIsDone(false);
    setSummary(null);
    setIsRunning(true);
    cancelledRef.current = false;

    const push = (type, message) =>
      setLines((prev) => [...prev, { type, message }]);

    (async () => {
      let updated = 0;
      let skipped = 0;
      let missing = 0;

      // ── Phase 1: AI maps all unique designations to departments ──
      const uniqueDesignations = [
        ...new Set(
          employees
            .map((e) => (e.designation || "").trim())
            .filter((designation) => !isMissingDesignation(designation))
        ),
      ];

      push("info", `Phase 1 — AI analysing ${uniqueDesignations.length} unique designation(s)…`);

      let aiMapping = {};   // { designation: { department_name, department_id } }
      let deptCreated = 0;

      try {
        const result = await aiSyncDepartments(uniqueDesignations);
        aiMapping = result.mapping || {};
        deptCreated = (result.created || []).length;

        if (deptCreated > 0) {
          push("updated", `AI created ${deptCreated} new department(s): ${result.created.join(", ")}`);
        }

        // Log a sample of AI decisions
        for (const [designation, info] of Object.entries(aiMapping).slice(0, 8)) {
          push("info", `"${designation}" → "${info.department_name}"`);
        }
        if (Object.keys(aiMapping).length > 8) {
          push("info", `…and ${Object.keys(aiMapping).length - 8} more mapped by AI`);
        }
      } catch (err) {
        const detail = err?.response?.data?.detail || err.message || "Unknown error";
        push("missing", `AI mapping failed: ${detail}`);
        push("info", "Tip: ensure the backend is running and openai package is installed (pip install openai)");
      }

      // Refresh department list to include AI-created departments with their IDs
      let liveDepts = departments;
      try {
        liveDepts = await getDepartments();
      } catch {
        push("missing", "Could not refresh departments — using local cache");
      }

      let naDepartment =
        liveDepts.find((d) => ["na", "n/a"].includes((d.department_name || "").trim().toLowerCase())) || null;

      if (!naDepartment) {
        try {
          naDepartment = await createDepartment({ department_name: "NA", description: "Not Applicable" });
          liveDepts = [...liveDepts, naDepartment];
          push("updated", "Created department \"NA\" for employees without designation");
        } catch (error) {
          const detail = error?.response?.data?.detail || error?.message || "Unknown error";
          push("missing", `Could not create NA department: ${detail}`);
        }
      }

      // ── Phase 2: update each employee using AI mapping ──
      push("info", `Phase 2 — syncing department for ${employees.length} employee(s)…`);

      for (const emp of employees) {
        if (cancelledRef.current) break;

        const designation = (emp.designation || "").trim();
        let targetDeptId = null;
        let targetDeptName = "";

        if (isMissingDesignation(designation)) {
          if (!naDepartment?.department_id) {
            push("missing", `${emp.employee_name} — no designation set and NA department unavailable`);
            missing += 1;
            continue;
          }
          targetDeptId = naDepartment.department_id;
          targetDeptName = naDepartment.department_name || "NA";
        } else {
          const aiResult = aiMapping[designation];
          targetDeptId = aiResult?.department_id;
          targetDeptName = aiResult?.department_name || "";

          if (!targetDeptId) {
            push("missing", `${emp.employee_name} — AI did not map "${designation}"`);
            missing += 1;
            continue;
          }
        }

        if (targetDeptId === emp.department_id) {
          push("skipped", `${emp.employee_name} — already in "${targetDeptName}"`);
          skipped += 1;
          continue;
        }

        try {
          await updateEmployee(emp.employee_id, {
            employee_name: emp.employee_name,
            email: emp.email,
            designation: emp.designation || null,
            department_id: targetDeptId,
            role_name: emp.role_name || "member",
            manager_id: emp.manager_id || null,
          });
          push("updated", `${emp.employee_name} → "${targetDeptName}"`);
          updated += 1;
        } catch {
          push("missing", `${emp.employee_name} — update failed`);
          missing += 1;
        }
      }

      if (!cancelledRef.current) {
        setSummary({ updated, skipped, missing, deptCreated });
        setIsDone(true);
        onDone?.();
      }

      setIsRunning(false);
    })();

    return () => { cancelledRef.current = true; };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    cancelledRef.current = true;
    onClose();
  };

  return (
    <Dialog open={open} onClose={isDone ? handleClose : undefined} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
        Sync Employee Departments
      </DialogTitle>

      {isRunning && <LinearProgress />}

      <DialogContent sx={{ pt: 1.5 }}>
        <Box
          sx={{
            bgcolor: "background.default",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            p: 1.5,
            minHeight: 180,
            maxHeight: 340,
            overflowY: "auto",
          }}
        >
          {lines.length === 0 && isRunning && (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={14} />
              <Typography fontSize={13} color="text.secondary">Starting…</Typography>
            </Box>
          )}

          {lines.map((event, idx) => (
            <LogLine key={idx} event={event} />
          ))}

          <div ref={logEndRef} />
        </Box>

        {isDone && summary && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography fontSize={13} fontWeight={700} mb={1}>Sync complete</Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              {summary.deptCreated > 0 && (
                <Chip label={`${summary.deptCreated} dept(s) created`} size="small"
                  sx={{ bgcolor: "#f5f3ff", color: "#7c3aed", fontWeight: 600 }} />
              )}
              <Chip label={`${summary.updated} employees updated`} size="small"
                sx={{ bgcolor: "#eff6ff", color: "#2563eb", fontWeight: 600 }} />
              <Chip label={`${summary.skipped} already correct`} size="small"
                sx={{ bgcolor: "#f0fdf4", color: "#16a34a", fontWeight: 600 }} />
              <Chip label={`${summary.missing} no match`} size="small"
                sx={{ bgcolor: "#fff7ed", color: "#c2410c", fontWeight: 600 }} />
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <AppButton variant="contained" onClick={handleClose} disabled={isRunning && !isDone}>
          {isDone ? "Close" : "Cancel"}
        </AppButton>
      </DialogActions>
    </Dialog>
  );
}

export default EmployeeDeptSyncDialog;
